import { useEffect, useState, Fragment } from 'react';
import { X, Truck, FloppyDisk, ArrowsLeftRight, EnvelopeSimple, Clock } from '@phosphor-icons/react';
import API, { fmt } from '@/lib/api';
import { mergeFeatures } from '@/lib/features';

/**
 * BestelLeverancierModal — toont artikelen onder min_voorraad per leverancier
 * en laat aanpassen + opslaan als aankoopbestelling.
 */
export default function BestelLeverancierModal({ open, onClose, groep, bekendeLeveranciers = [], onSaved }) {
  const [regels, setRegels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  // Ontvanger van deze bestelling — begint met de groep-leverancier
  const [ontvanger, setOntvanger] = useState({ naam: '', id: null, email: '' });
  const [altOpenIdx, setAltOpenIdx] = useState(null);
  // Verzend-opties (na opslaan)
  const [sendEmail, setSendEmail] = useState('');
  const [sendDeadline, setSendDeadline] = useState(48);
  const [sendBericht, setSendBericht] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [features, setFeatures] = useState(() => mergeFeatures({}));

  useEffect(() => {
    if (!open) return;
    API.get('/instellingen').then(r => setFeatures(mergeFeatures(r.data?.features || {}))).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open || !groep) return;
    setRegels((groep.artikelen || []).map(a => ({
      art_id: a.id, art_code: a.art_code || '', ean: a.ean || '',
      omschrijving: a.omschrijving || '',
      aantal: a.suggestie || a.min_voorraad || 1,
      prijs: a.aankoopprijs || 0,
      leveringstermijn_dagen: a.leveringstermijn_dagen ?? null,
      moq: a.moq || 0,
      origineel_leverancier: a.leverancier || '', // primary leverancier (goedkoopste)
      alternatieven: a.alternatieven || [],       // andere leveranciers + prijs/termijn/MOQ
    })));
    setOntvanger({
      naam: groep.leverancier === 'Onbekend' ? '' : (groep.leverancier || ''),
      id: groep.leverancier_id || null,
      email: groep.email || '',
    });
    setSaved(null);
    setAltOpenIdx(null);
    setSendResult(null);
    setSendEmail('');
  }, [open, groep]);

  if (!open || !groep) return null;

  const totaal = regels.reduce((s, r) => s + (parseFloat(r.aantal) || 0) * (parseFloat(r.prijs) || 0), 0);
  const ontvangerRequired = groep.leverancier === 'Onbekend' && !ontvanger.naam;
  const anyMismatch = regels.some(r => r.origineel_leverancier && ontvanger.naam && r.origineel_leverancier !== ontvanger.naam);

  const pickLeverancier = (lev) => {
    if (!lev) { setOntvanger({ naam: '', id: null, email: '' }); return; }
    setOntvanger({ naam: lev.bedrijf, id: lev.id || null, email: lev.email || '' });
  };

  const updRegel = (i, k, v) => setRegels(p => p.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const rmRegel = (i) => setRegels(p => p.filter((_, j) => j !== i));

  const save = async () => {
    const reg = regels.filter(r => (parseFloat(r.aantal) || 0) > 0);
    if (!reg.length) { alert('Geen regels om te bestellen'); return; }
    if (!ontvanger.naam) { alert('Kies eerst een leverancier voor deze bestelling.'); return; }
    if (anyMismatch) {
      const bevestig = window.confirm(
        `Let op: sommige artikelen in deze bestelling hebben een andere leverancier ingesteld dan "${ontvanger.naam}".\n\n` +
        `Wil je toch doorgaan? De bestelling wordt enkel verstuurd naar "${ontvanger.naam}".`
      );
      if (!bevestig) return;
    }
    setSaving(true);
    try {
      const r = await API.post('/voorraad/bestel-bij-leverancier', {
        leverancier: ontvanger.naam,
        leverancier_id: ontvanger.id,
        regels: reg.map(x => ({
          art_id: x.art_id, art_code: x.art_code, ean: x.ean,
          omschrijving: x.omschrijving,
          aantal: parseFloat(x.aantal) || 0,
          prijs: parseFloat(x.prijs) || 0,
        })),
      });
      setSaved(r.data);
      // Vul e-mail van leverancier standaard in
      setSendEmail(ontvanger.email || '');
      onSaved?.(r.data);
    } catch (e) {
      alert('Opslaan mislukt: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const printPdf = () => {
    if (!saved) return;
    const w = window.open('', '_blank');
    const lijnenHtml = saved.lijnen.map(l => `<tr>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;font-family:monospace;font-size:11px">${l.art_code || '-'}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;font-size:12px">${l.omschrijving}${l.ean ? `<div style="font-size:9px;color:#6b7280;font-family:monospace">EAN: ${l.ean}</div>` : ''}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right">${l.aantal}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right">${fmt(l.prijs)}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right;font-weight:600">${fmt(l.totaal)}</td>
    </tr>`).join('');
    w.document.write(`<!DOCTYPE html><html><head><title>${saved.id}</title>
<style>body{font-family:'Segoe UI',Arial,sans-serif;padding:40px;max-width:820px;margin:0 auto;color:#1a1a1a}table{width:100%;border-collapse:collapse}th{background:#f8f9fa;padding:8px;text-align:left;font-size:11px;text-transform:uppercase;border-bottom:2px solid #dee2e6;color:#6c757d}@media print{.no-print{display:none}}</style></head><body>
<div style="display:flex;justify-content:space-between;margin-bottom:24px;border-bottom:3px solid #002FA7;padding-bottom:14px">
  <div>
    <div style="font-size:22px;font-weight:800;color:#002FA7">Aankoopbestelling</div>
    <div style="font-size:13px;color:#666;margin-top:4px">${saved.id}</div>
  </div>
  <div style="text-align:right">
    <div style="font-size:11px;color:#6b7280">Datum: ${saved.datum}</div>
    <div style="font-size:11px;color:#6b7280">Status: Concept</div>
  </div>
</div>
<div style="background:#f8f9fa;padding:12px 16px;margin-bottom:20px;font-size:12px">
  <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:.08em;margin-bottom:4px">Leverancier</div>
  <div style="font-weight:600;font-size:14px">${saved.leverancier}</div>
</div>
<table><thead><tr>
  <th>Art. code</th><th>Omschrijving</th>
  <th style="text-align:right">Aantal</th><th style="text-align:right">Prijs</th><th style="text-align:right">Totaal</th>
</tr></thead><tbody>${lijnenHtml}</tbody></table>
<div style="margin-top:20px;text-align:right;border-top:2px solid #dee2e6;padding-top:12px">
  <div style="font-size:12px;color:#666">Excl. BTW: ${fmt(saved.excl)}</div>
  <div style="font-size:12px;color:#666">BTW (21%): ${fmt(saved.btw_totaal)}</div>
  <div style="font-size:22px;font-weight:800;margin-top:4px">Totaal: ${fmt(saved.totaal)}</div>
</div>
${saved.nota ? `<div style="margin-top:20px;padding:12px;background:#F0F4FF;border-left:3px solid #002FA7;font-size:12px">${saved.nota}</div>` : ''}
<div class="no-print" style="margin-top:30px;text-align:center"><button onclick="window.print()" style="padding:12px 32px;background:#002FA7;color:#fff;border:none;cursor:pointer;font-size:14px;font-weight:600">Afdrukken / Opslaan als PDF</button></div>
</body></html>`);
    w.document.close();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="bestel-modal">
      <div className="bg-white w-[min(820px,95vw)] max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2"><Truck size={20} className="text-[#002FA7]" weight="duotone" /><h3 className="font-semibold text-sm">Aankoopbestelling aanmaken</h3></div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-900" data-testid="bestel-close"><X size={20} /></button>
        </div>

        {!saved ? (
          <>
            {/* Leverancier-selector bovenaan */}
            <div className={`px-5 py-3 border-b ${ontvangerRequired ? 'bg-amber-50 border-amber-200' : 'bg-[#F0F4FF] border-gray-200'}`} data-testid="bestel-ontvanger-box">
              <div className="flex items-center gap-3">
                <label className="text-[11px] uppercase tracking-wider font-bold text-gray-600 whitespace-nowrap">Bestellen bij</label>
                <select
                  className="field-input flex-1 font-semibold"
                  value={ontvanger.id || (ontvanger.naam ? `__naam:${ontvanger.naam}` : '')}
                  onChange={e => {
                    const v = e.target.value;
                    if (!v) { pickLeverancier(null); return; }
                    if (v.startsWith('__naam:')) { setOntvanger({ naam: v.slice(7), id: null, email: '' }); return; }
                    const lev = bekendeLeveranciers.find(l => l.id === v);
                    if (lev) pickLeverancier(lev);
                  }}
                  data-testid="bestel-leverancier-select"
                >
                  <option value="">— Kies een leverancier —</option>
                  {ontvanger.naam && !ontvanger.id && <option value={`__naam:${ontvanger.naam}`}>{ontvanger.naam} (vrije tekst — niet in leveranciers)</option>}
                  {bekendeLeveranciers.map(l => (
                    <option key={l.id} value={l.id}>{l.bedrijf}{l.email ? ` — ${l.email}` : ''}</option>
                  ))}
                </select>
              </div>
              {ontvangerRequired && (
                <div className="mt-2 text-[11px] text-amber-800 font-medium">
                  ⚠ Deze artikelen hebben geen leverancier. Kies er één voor je de bestelling opslaat.
                </div>
              )}
              {ontvanger.naam && !ontvangerRequired && (
                <div className="mt-2 text-[11px] text-gray-600">
                  {ontvanger.email ? <>E-mail: <strong>{ontvanger.email}</strong></> : 'Geen e-mail voor leverancier ingesteld — PDF kan gedownload en manueel verstuurd worden.'}
                </div>
              )}
              {anyMismatch && (
                <div className="mt-2 text-[11px] text-amber-800 bg-amber-100 border border-amber-300 px-2 py-1.5" data-testid="bestel-mismatch-warning">
                  ⚠ <strong>Meerdere leveranciers</strong> onder deze regels. Bekijk elke regel zorgvuldig — de bestelling gaat enkel naar <strong>{ontvanger.naam}</strong>. Verwijder regels die niet thuishoren met de ✕-knop.
                </div>
              )}
            </div>

            <div className="px-5 pt-3 text-xs text-gray-500">
              Controleer aantallen en aankoopprijzen, pas aan indien nodig, en sla de bestelling op.
            </div>
            <div className="p-5 overflow-y-auto">
              <table className="data-table text-xs">
                <thead>
                  <tr>
                    <th>Art. code</th><th>Omschrijving</th>
                    <th style={{ width: 130 }}>Leverancier op artikel</th>
                    <th className="right" style={{ width: 76 }}>Termijn</th>
                    <th className="right" style={{ width: 56 }}>MOQ</th>
                    <th style={{ width: 50 }}>Alt.</th>
                    <th className="right" style={{ width: 90 }}>Aantal</th>
                    <th className="right" style={{ width: 110 }}>Prijs (excl.)</th>
                    <th className="right" style={{ width: 100 }}>Totaal</th>
                    <th style={{ width: 30 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {regels.map((r, i) => {
                    const mismatch = ontvanger.naam && r.origineel_leverancier && r.origineel_leverancier !== ontvanger.naam;
                    const hasAlt = (r.alternatieven || []).length > 0;
                    const belowMoq = r.moq > 0 && (parseFloat(r.aantal) || 0) < r.moq;
                    return (
                      <Fragment key={i}>
                      <tr className={mismatch ? 'bg-amber-50' : ''}>
                        <td className="font-mono text-[11px] text-[#002FA7] font-semibold">{r.art_code || '-'}</td>
                        <td>{r.omschrijving}{r.ean && <div className="text-[10px] text-gray-500 font-mono">EAN: {r.ean}</div>}</td>
                        <td>
                          {r.origineel_leverancier ? (
                            <span className={`inline-block text-[10px] px-1.5 py-0.5 font-semibold ${mismatch ? 'bg-amber-100 text-amber-900 border border-amber-300' : 'bg-gray-100 text-gray-700'}`} title={mismatch ? `Verschilt van ontvanger: ${ontvanger.naam}` : ''}>
                              {mismatch && '⚠ '}{r.origineel_leverancier}
                            </span>
                          ) : (
                            <span className="text-[10px] text-gray-400 italic">—</span>
                          )}
                        </td>
                        <td className="right text-[11px] text-gray-600">{r.leveringstermijn_dagen != null && r.leveringstermijn_dagen !== '' ? `${r.leveringstermijn_dagen}d` : '—'}</td>
                        <td className={`right text-[11px] ${belowMoq ? 'text-amber-700 font-semibold' : 'text-gray-600'}`} title={belowMoq ? 'Aantal onder MOQ' : ''}>{r.moq > 0 ? r.moq : '—'}</td>
                        <td>
                          {hasAlt ? (
                            <button
                              type="button"
                              className="text-[10px] px-1.5 py-0.5 border border-gray-300 hover:border-[#002FA7] hover:text-[#002FA7] inline-flex items-center gap-0.5"
                              onClick={() => setAltOpenIdx(altOpenIdx === i ? null : i)}
                              data-testid={`bestel-alt-btn-${i}`}
                              title={`${r.alternatieven.length} alternatieve leverancier(s)`}
                            >
                              <ArrowsLeftRight size={10} /> {r.alternatieven.length}
                            </button>
                          ) : (
                            <span className="text-[10px] text-gray-300">—</span>
                          )}
                        </td>
                        <td><input type="number" className="field-input text-right" value={r.aantal} min="0" onChange={e => updRegel(i, 'aantal', e.target.value)} data-testid={`bestel-aantal-${i}`} /></td>
                        <td><input type="number" className="field-input text-right" value={r.prijs} step="0.01" min="0" onChange={e => updRegel(i, 'prijs', e.target.value)} data-testid={`bestel-prijs-${i}`} /></td>
                        <td className="right font-semibold">{fmt((parseFloat(r.aantal) || 0) * (parseFloat(r.prijs) || 0))}</td>
                        <td><button onClick={() => rmRegel(i)} className="text-gray-400 hover:text-red-600" title="Verwijder regel uit deze bestelling"><X size={14} /></button></td>
                      </tr>
                      {altOpenIdx === i && hasAlt && (
                        <tr className="bg-[#F0F4FF]">
                          <td colSpan="10" className="p-3">
                            <div className="text-[11px] font-semibold text-gray-700 mb-2">Alternatieve leveranciers voor dit artikel — klik op "Gebruik" om over te schakelen</div>
                            <table className="w-full text-[11px]">
                              <thead>
                                <tr className="text-gray-500">
                                  <th className="text-left py-1">Leverancier</th>
                                  <th className="right py-1">Aankoopprijs</th>
                                  <th className="right py-1">Levertijd</th>
                                  <th className="right py-1">MOQ</th>
                                  <th className="right py-1">Δ t.o.v. huidig</th>
                                  <th className="py-1"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {/* Huidige selectie tonen als eerste rij (highlighted) */}
                                <tr className="bg-white">
                                  <td className="py-1 font-semibold">{r.origineel_leverancier} <span className="text-[9px] text-green-700 font-bold ml-1">✓ HUIDIG</span></td>
                                  <td className="right font-semibold">{fmt(r.prijs)}</td>
                                  <td className="right">{r.leveringstermijn_dagen != null && r.leveringstermijn_dagen !== '' ? `${r.leveringstermijn_dagen}d` : '—'}</td>
                                  <td className="right">{r.moq > 0 ? r.moq : '—'}</td>
                                  <td className="right text-gray-400">—</td>
                                  <td></td>
                                </tr>
                                {r.alternatieven.map((alt, k) => {
                                  const delta = (alt.aankoopprijs || 0) - (parseFloat(r.prijs) || 0);
                                  const goedkoper = delta < 0;
                                  return (
                                    <tr key={k} className="hover:bg-white">
                                      <td className="py-1">{alt.bedrijf}</td>
                                      <td className="right">{fmt(alt.aankoopprijs)}</td>
                                      <td className="right">{alt.leveringstermijn_dagen != null ? `${alt.leveringstermijn_dagen}d` : '—'}</td>
                                      <td className="right">{alt.moq > 0 ? alt.moq : '—'}</td>
                                      <td className={`right font-semibold ${goedkoper ? 'text-green-700' : delta > 0 ? 'text-red-700' : 'text-gray-400'}`}>
                                        {delta === 0 ? '±0' : (goedkoper ? '' : '+') + fmt(delta)}
                                      </td>
                                      <td className="right">
                                        <button
                                          type="button"
                                          className="text-[10px] px-2 py-0.5 bg-[#002FA7] text-white font-semibold hover:bg-[#0040D0]"
                                          onClick={() => {
                                            // Schakel regel over naar deze alternatief — update prijs, origineel_leverancier, termijn, MOQ
                                            // Maak deze alternatief tot "huidig" en verplaats het oude huidige naar de alternatieven-lijst
                                            setRegels(prev => prev.map((rr, j) => {
                                              if (j !== i) return rr;
                                              const oud = {
                                                leverancier_id: null,
                                                bedrijf: rr.origineel_leverancier,
                                                aankoopprijs: parseFloat(rr.prijs) || 0,
                                                leveringstermijn_dagen: rr.leveringstermijn_dagen,
                                                moq: rr.moq,
                                              };
                                              const nieuweAlts = rr.alternatieven.filter((_, kk) => kk !== k);
                                              // Voeg het oude huidige toe als alternatief, enkel als het een naam had
                                              if (oud.bedrijf) nieuweAlts.push(oud);
                                              return {
                                                ...rr,
                                                prijs: alt.aankoopprijs || 0,
                                                origineel_leverancier: alt.bedrijf,
                                                leveringstermijn_dagen: alt.leveringstermijn_dagen,
                                                moq: alt.moq || 0,
                                                alternatieven: nieuweAlts,
                                              };
                                            }));
                                            setAltOpenIdx(null);
                                          }}
                                          data-testid={`bestel-alt-use-${i}-${k}`}
                                        >Gebruik</button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            <div className="text-[10px] text-gray-500 mt-2 italic">Tip: na switch — als de nieuwe leverancier verschilt van de ontvanger boven, verschijnt een ⚠-badge en de bestelling gaat enkel naar de ontvanger. Verwijder de regel en open een nieuwe bestelling bij de juiste leverancier.</div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
              <div className="mt-4 text-right text-sm">
                <span className="text-gray-500">Totaal excl.: </span><strong>{fmt(totaal)}</strong>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
              <button onClick={onClose} className="btn btn-ghost btn-sm" data-testid="bestel-cancel">Annuleren</button>
              <button onClick={save} disabled={saving || ontvangerRequired} className="btn btn-primary btn-sm inline-flex items-center gap-1.5" data-testid="bestel-save"><FloppyDisk size={15} /> {saving ? 'Opslaan…' : 'Bestelling opslaan'}</button>
            </div>
          </>
        ) : (
          <div className="p-6 space-y-4">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-100 text-green-700 text-2xl">✓</div>
              <h4 className="font-semibold text-gray-900 mt-2">Bestelling opgeslagen</h4>
              <div className="font-mono text-[#002FA7]" data-testid="bestel-saved-id">{saved.id}</div>
              <div className="text-sm text-gray-600">{saved.lijnen.length} regel(s) · Totaal {fmt(saved.totaal)} incl. BTW</div>
            </div>

            {!sendResult ? (
              features.po_email_verzenden?.actief ? (
              <div className="border border-gray-200 p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <EnvelopeSimple size={18} className="text-[#002FA7]" weight="duotone" />
                  Verzenden naar leverancier
                </div>
                <div className="text-[11px] text-gray-500">De leverancier krijgt de bestelling als HTML-bijlage + een knop om online te bevestigen of aan te passen.</div>
                <div>
                  <label className="text-[11px] text-gray-600 font-semibold">E-mailadres leverancier</label>
                  <input
                    type="email"
                    className="field-input"
                    value={sendEmail}
                    onChange={e => setSendEmail(e.target.value)}
                    placeholder="bestellingen@leverancier.be"
                    data-testid="bestel-send-email"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-gray-600 font-semibold inline-flex items-center gap-1"><Clock size={12} /> Bevestigingsdeadline (uren)</label>
                    <input
                      type="number"
                      min="0"
                      max="720"
                      className="field-input"
                      value={sendDeadline}
                      onChange={e => setSendDeadline(e.target.value)}
                      data-testid="bestel-send-deadline"
                    />
                    <div className="text-[10px] text-gray-400 mt-0.5">0 = geen deadline. Standaard: 48u.</div>
                  </div>
                  <div>
                    <label className="text-[11px] text-gray-600 font-semibold">Extra bericht (optioneel)</label>
                    <textarea
                      className="field-input"
                      rows="2"
                      value={sendBericht}
                      onChange={e => setSendBericht(e.target.value)}
                      placeholder="Bv. gewenste leveringsdatum…"
                      data-testid="bestel-send-bericht"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button onClick={printPdf} className="btn btn-ghost btn-sm border border-gray-300" data-testid="bestel-print">Alleen PDF</button>
                  <button
                    onClick={async () => {
                      if (!sendEmail.trim()) { alert('Vul eerst een e-mailadres in'); return; }
                      setSending(true);
                      try {
                        const r = await API.post(`/aankoopbestellingen/${saved.id}/verzenden`, {
                          to: sendEmail.trim(),
                          deadline_uren: parseInt(sendDeadline) || 0,
                          bericht: sendBericht.trim(),
                        });
                        setSendResult(r.data);
                      } catch (e) {
                        alert('Verzenden mislukt: ' + (e?.response?.data?.detail || e.message));
                      } finally { setSending(false); }
                    }}
                    disabled={sending || !sendEmail.trim()}
                    className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
                    data-testid="bestel-send"
                  >
                    <EnvelopeSimple size={14} /> {sending ? 'Verzenden…' : 'Nu verzenden'}
                  </button>
                </div>
              </div>
              ) : (
                <div className="border border-gray-200 bg-gray-50 p-4 text-center">
                  <div className="text-[12px] text-gray-600 mb-2">De functie "PO per e-mail verzenden" staat uitgeschakeld.</div>
                  <button onClick={printPdf} className="btn btn-primary btn-sm" data-testid="bestel-print-only">Afdrukken / PDF</button>
                </div>
              )
            ) : (
              <div className="border border-green-200 bg-green-50 p-4 space-y-2" data-testid="bestel-send-result">
                <div className="flex items-center gap-2 text-sm font-semibold text-green-900">✓ Verzonden</div>
                <div className="text-[12px] text-green-800">De leverancier krijgt een e-mail met een link om online te bevestigen of aan te passen.</div>
                {sendResult.deadline && (
                  <div className="text-[11px] text-green-700">Bevestigingsdeadline: {new Date(sendResult.deadline).toLocaleString('nl-BE')}</div>
                )}
                {sendResult.confirm_url && (
                  <div className="text-[11px]">
                    <span className="text-gray-600">Bevestigings-link: </span>
                    <a href={sendResult.confirm_url} target="_blank" rel="noreferrer" className="text-[#002FA7] underline font-mono break-all">{sendResult.confirm_url}</a>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-end pt-2">
              <button onClick={onClose} className="btn btn-primary btn-sm" data-testid="bestel-done">Sluiten</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
